import React, { useEffect } from "react";
import Pagination from "../common/Pagination";
import { Link } from "react-router-dom";
import {
  ArrowUp,
  ArrowUpNarrowWide,
  CheckCircle,
  ChevronsDown,
  ChevronsUp,
  SortAscIcon,
  UserLock,
  UserRoundPen,
  UserRoundXIcon,
  ShieldOff,
  XCircle,
  AlertTriangle,
} from "lucide-react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import { useAuth } from "../../context/AuthContext";
import { getUserType } from "../../helpers/Users";
import { renderSortField, stringSlip } from "../common/RenderFunctions";
import ModalScreen from "../common/ModalScreen";

interface ListItemsProps {
  userProfile: ProfileAndSettings | null;
  data: User[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  roleFilterSelected: string;
  setRoleFilterSelected: (role: string) => void;
  commercialAgentFilterSelected: string;
  setCommercialAgentFilterSelected: (agent: string) => void;
  currentPage: number;
  perPage?: number;
  setPerPage?: (perPage: number) => void;
  totalPages: number;
  sortBy: string;
  sortOrder: "asc" | "desc";
  setSortBy: (sortBy: string) => void;
  setSortOrder: (sortOrder: "asc" | "desc") => void;
  onPageChange: (page: number) => void;
  setError: (error: string | null) => void;
  setCheckUser: (checkUser: boolean) => void;
  handleManageItem: (item: User) => void;
  handleDeleteItem: (item: User) => void;
  handlePasswordReset: (item: User) => void;
  handleMfaReset?: (item: User) => void;
  handleCreateItem: () => void;
  remainingItems: number;
}

const ListItems: React.FC<ListItemsProps> = ({
  userProfile,
  data,
  searchTerm,
  setSearchTerm,
  roleFilterSelected,
  setRoleFilterSelected,
  commercialAgentFilterSelected,
  setCommercialAgentFilterSelected,
  currentPage,
  perPage,
  setPerPage,
  totalPages,
  sortBy,
  sortOrder,
  setSortBy,
  setSortOrder,
  onPageChange,
  setError,
  setCheckUser,
  handleManageItem,
  handleDeleteItem,
  handlePasswordReset,
  handleMfaReset,
  handleCreateItem,
}) => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [commercialAgents, setCommercialAgents] = React.useState<FieldValue[]>(
    [],
  );
  const [userToDelete, setUserToDelete] = React.useState<User | null>(null);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);

  const isMainAccount = userProfile?.isMainAccount() || false;
  const isAdmin = userProfile?.isAdmin() || false;
  const isCommercial = userProfile?.isCommercial() || false;
  const isAdminOrMainAccount = isAdmin || isMainAccount;
  const isAdminOrCommercial = isAdmin || isCommercial;
  const remainingSubaccountsAllowed =
    userProfile?.profile.remainingSubaccountsAllowed || 0;
  const isAllowedToCreateUser =
    isAdminOrMainAccount && remainingSubaccountsAllowed > 0;

  const handleDeleteClick = (item: User) => {
    setUserToDelete(item);
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = () => {
    if (!userToDelete) return;

    setShowDeleteModal(false);
    setLoading(true);
    api
      .delete(API_URLS.USERS + `${userToDelete.id}`)
      .then((response) => {
        if (response.status === 200) {
          handleDeleteItem(userToDelete);
        } else {
          console.warn("Erro ao apagar o utilizador:", response.data);
          setError("Erro ao apagar o utilizador. Por favor, tente novamente.");
        }
      })
      .catch((error) => {
        console.warn("Erro ao apagar o utilizador:", error);
        setError("Erro ao apagar o utilizador. Por favor, tente novamente.");
      })
      .finally(() => {
        setLoading(false);
        setUserToDelete(null);
      });
  };

  const fetchCommercialAgents = () => {
    setLoading(true);
    api
      .get(API_URLS.USERS + `_values/commercial_agents`)
      .then((response) => {
        if (response.status === 200) {
          setCommercialAgents(response.data);
        } else {
          console.warn("Erro ao buscar agentes comerciais:", response.data);
          setError(
            "Erro ao buscar agentes comerciais. Por favor, tente novamente.",
          );
        }
      })
      .catch((error) => {
        console.warn("Erro ao buscar agentes comerciais:", error);
        setError(
          "Erro ao buscar agentes comerciais. Por favor, tente novamente.",
        );
      })
      .finally(() => setLoading(false));
  };

  const handleSelectCommercialAgentFocus = (
    e: React.FocusEvent<HTMLSelectElement>,
  ) => {
    e.preventDefault();
    if (commercialAgents.length === 0) {
      fetchCommercialAgents();
    }
  };

  const renderSortButton = (field: string, label: string) =>
    renderSortField(sortBy, sortOrder, setSortOrder, setSortBy, field, label);

  const renderActionButtons = () => {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {isAdmin && (
            <button
              onClick={() => setCheckUser(true)}
              className="px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              aria-label="Validar utilizador"
            >
              Validar Utilizador
            </button>
          )}
          {(isAllowedToCreateUser || isAdmin) && (
            <button
              onClick={handleCreateItem}
              className="px-4 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
              aria-label="Adicionar novo utilizador"
            >
              Adicionar Utilizador
            </button>
          )}
        </div>
        {isMainAccount && (
          <span className="text-sm text-gray-600 bg-gray-100 px-4 py-2 rounded-lg">
            {remainingSubaccountsAllowed > 0
              ? `Restam ${remainingSubaccountsAllowed} utilizadores`
              : "Limite de utilizadores atingido"}
          </span>
        )}
      </div>
    );
  };

  const renderFilters = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Search Input */}
        <div className="md:col-span-1">
          <label
            htmlFor="search"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Pesquisar
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              id="search"
              name="search"
              placeholder="Nome, utilizador, email ou telefone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 placeholder-gray-500 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200"
            />
          </div>
        </div>

        {/* Role Filter */}
        <div>
          <label
            htmlFor="role"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Perfil
          </label>
          <select
            id="role"
            name="role"
            onChange={(e) => setRoleFilterSelected(e.target.value)}
            className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200"
            value={roleFilterSelected}
          >
            <option value="">Todos</option>
            {isAdmin && (
              <>
                <option value="admin">Administrador</option>
                <option value="commercial">Comercial</option>
              </>
            )}
            <option value="reseller">Revendedor</option>
            <option value="user">Convidado</option>
          </select>
        </div>

        {/* Commercial Agent Filter (Admin only) */}
        {isAdmin && (
          <div>
            <label
              htmlFor="commercial"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Comercial
            </label>
            <select
              id="commercial"
              name="commercial_agent"
              onChange={(e) => setCommercialAgentFilterSelected(e.target.value)}
              className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all duration-200"
              value={commercialAgentFilterSelected}
              onFocus={handleSelectCommercialAgentFocus}
            >
              <option value="">Todos</option>
              {commercialAgents.map((agent) => (
                <option
                  key={agent.value?.toString()}
                  value={agent.value?.toString()}
                >
                  {agent.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6 mb-6">
        {/* Action Buttons */}
        {renderActionButtons()}

        {/* Filters */}
        {renderFilters()}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-auto">
          <table
            className="min-w-full divide-y divide-gray-200"
            style={{ width: "auto" }}
          >
            <thead className="bg-gray-50">
              <tr>
                {isAdminOrCommercial && (
                  <th
                    className="px-3 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider"
                    align="center"
                  >
                    {renderSortButton("erp_client_id", "Cliente")}
                  </th>
                )}
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  {renderSortButton("username", "Utilizador")}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {renderSortButton("first_name", "Nome")}
                </th>
                {isAdminOrCommercial && (
                  <th
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    align="center"
                  >
                    {renderSortButton("company", "Empresa")}
                  </th>
                )}
                {isAdminOrCommercial && (
                  <th
                    className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    align="center"
                  >
                    {renderSortButton(
                      "commercial_agent_ref",
                      isAdmin ? "Comercial" : "Cod. Comercial",
                    )}
                  </th>
                )}
                <th
                  className="px-3 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider"
                  align="center"
                >
                  {renderSortButton("role", "Perfil")}
                </th>
                <th
                  className="px-3 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider"
                  align="center"
                >
                  {renderSortButton("last_login_at", "Ultimo Login")}
                </th>
                <th
                  className="px-3 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-24"
                  align="center"
                >
                  Estado
                </th>
                {isAdminOrMainAccount && (
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((item: User, k: number) => {
                if (item)
                  return (
                    <tr key={k} className="hover:bg-gray-50 transition-colors">
                      {isAdminOrCommercial && (
                        <td
                          className="px-3 py-3 whitespace-nowrap text-sm text-gray-900"
                          align="center"
                        >
                          {item.erpUserReference ?? "N/A"}
                          {item.isMainAccount && (
                            <>
                              <br />
                              <span className="text-gray-500 text-xs">
                                Conta Principal
                              </span>
                            </>
                          )}
                        </td>
                      )}
                      <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900 w-32">
                        {item.username}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                        {(item.firstName?.length ?? 0) >
                        (isAdminOrCommercial ? 15 : 45)
                          ? item.firstName?.substring(
                              0,
                              isAdminOrCommercial ? 15 : 45,
                            ) + "..."
                          : item.firstName || "N/A"}
                      </td>
                      {isAdminOrCommercial && (
                        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                          {(item.client?.name?.length ?? 0) >
                          (isAdminOrCommercial ? 32 : 45)
                            ? item.client?.name?.substring(
                                0,
                                isAdminOrCommercial ? 32 : 45,
                              ) + "..."
                            : item.client?.name || "N/A"}
                        </td>
                      )}
                      {isAdminOrCommercial && (
                        <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-900">
                          {item.erpUserReference == "NIPOSOM" ? (
                            <span className="text-xs">n/a</span>
                          ) : (item.commercial_agent_id ?? 0) > 0 ? (
                            <>
                              {isAdmin && (
                                <>
                                  <span>
                                    {(
                                      item.commercial_agent_name ?? ""
                                    ).trim() != "" ? (
                                      stringSlip(
                                        item.commercial_agent_name ?? "",
                                        32,
                                      )
                                    ) : (
                                      <small className="text-xs">
                                        {item.commercial_agent_email}
                                      </small>
                                    )}
                                  </span>
                                  {" - "}
                                </>
                              )}
                              <small className="text-xs font-mono px-3">
                                {(
                                  item.commercial_agent_portfolio ?? ""
                                ).trim() != ""
                                  ? item.commercial_agent_portfolio
                                  : "-"}
                              </small>
                            </>
                          ) : (
                            <span className="text-xs">
                              {(item.commercial_agent_portfolio ?? "").trim() !=
                              ""
                                ? item.commercial_agent_portfolio
                                : "-"}
                            </span>
                          )}
                        </td>
                      )}
                      <td
                        className="px-3 py-3 whitespace-nowrap text-sm text-gray-900"
                        align="center"
                      >
                        {getUserType(item)}
                      </td>
                      <td
                        className="px-3 py-3 whitespace-nowrap text-sm text-gray-500"
                        align="center"
                      >
                        <span className="text-xs font-mono">
                          {item.lastLoginAt ? (
                            new Date(item.lastLoginAt || "").toLocaleDateString(
                              "pt-PT",
                              {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )
                          ) : (
                            <i>Ainda não fez login</i>
                          )}
                        </span>
                      </td>
                      <td
                        className="px-3 py-3 whitespace-nowrap text-sm text-gray-900 w-24"
                        align="center"
                      >
                        {item.locked ? (
                          <XCircle size={24} className="has-text-danger" />
                        ) : (
                          <CheckCircle size={24} className="has-text-success" />
                        )}
                      </td>
                      {isAdminOrMainAccount && (
                        <td className="px-3 py-3 whitespace-nowrap text-sm font-medium">
                          <Link
                            to={`#`}
                            aria-label="Gerir utilizador"
                            onClick={(e) => {
                              e.preventDefault();
                              handleManageItem(item);
                            }}
                            className="text-green-600 hover:text-green-900 transition-colors"
                          >
                            <span className="inline-flex items-center">
                              <UserRoundPen size={20} />
                            </span>
                          </Link>
                          <Link
                            to={`#`}
                            aria-label="Redefinir senha"
                            onClick={(e) => {
                              e.preventDefault();
                              handlePasswordReset(item);
                            }}
                            className="text-orange-500 hover:text-orange-700 transition-colors ml-3"
                          >
                            <span className="inline-flex items-center">
                              <UserLock size={20} />
                            </span>
                          </Link>
                          {isAdmin && handleMfaReset && (
                            <Link
                              to={`#`}
                              aria-label="Repor MFA"
                              onClick={(e) => {
                                e.preventDefault();
                                handleMfaReset(item);
                              }}
                              className="text-purple-600 hover:text-purple-900 transition-colors ml-3"
                              title="Repor MFA (Verificação em 2 Passos)"
                            >
                              <span className="inline-flex items-center">
                                <ShieldOff size={20} />
                              </span>
                            </Link>
                          )}
                          {isAdmin && (
                            <Link
                              to={`#`}
                              aria-label="Apagar utilizador"
                              onClick={(e) => {
                                e.preventDefault();
                                handleDeleteClick(item);
                              }}
                              className="text-red-600 hover:text-red-900 transition-colors ml-3"
                            >
                              <span className="inline-flex items-center">
                                <UserRoundXIcon size={20} />
                              </span>
                            </Link>
                          )}
                        </td>
                      )}
                    </tr>
                  );
              })}
              {data.length === 0 && (
                <tr>
                  <td
                    colSpan={isAdmin ? 9 : isMainAccount ? 6 : 8}
                    className="text-center py-4"
                  >
                    {searchTerm ? (
                      <>Nenhum utilizador encontrado.</>
                    ) : (
                      <>Nenhum utilizador cadastrado.</>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-center items-center m-4">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            perPage={perPage}
            setPerPage={setPerPage}
            onPageChange={onPageChange}
          />
        </div>
      </div>

      {showDeleteModal && userToDelete && (
        <ModalScreen
          title="Confirmar Exclusão"
          onClose={() => setShowDeleteModal(false)}
          state="alert"
          footer={
            <div className="flex justify-end gap-2 p-4">
              <button
                className="px-4 py-2 bg-white text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-colors"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancelar
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors flex items-center"
                onClick={confirmDeleteUser}
              >
                <span className="mr-2">
                  <UserRoundXIcon size={18} />
                </span>
                Apagar Utilizador
              </button>
            </div>
          }
        >
          <div className="p-6">
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Atenção: Ação Irreversível
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>
                      Tem certeza que deseja apagar o utilizador{" "}
                      <strong>{userToDelete.username}</strong>?
                    </p>
                    <p className="mt-1">
                      Esta ação não pode ser desfeita e o utilizador perderá o
                      acesso ao sistema imediatamente.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ModalScreen>
      )}
    </>
  );
};

export default ListItems;
